const API_URL = import.meta.env.VITE_API_URL || '';
const API_KEY = import.meta.env.VITE_API_KEY || '';

// Token management
let authToken = null;
let onSessionKicked = null;

export const setSessionKickedCallback = (callback) => {
  onSessionKicked = callback;
};

const getStoredToken = () => {
  return localStorage.getItem('auth-token');
};

const setStoredToken = (token) => {
  localStorage.setItem('auth-token', token);
  authToken = token;
};

const removeStoredToken = () => {
  localStorage.removeItem('auth-token');
  authToken = null;
};

// Initialize token from storage
const initToken = () => {
  if (!authToken) {
    authToken = getStoredToken();
  }
  return authToken;
};

// Base fetch wrapper
const apiFetch = async (endpoint, options = {}) => {
  initToken();

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  if (API_KEY) {
    headers['x-api-key'] = API_KEY;
  }

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
  });

  // Session kicked or expired
  if (response.status === 401 || response.status === 403) {
    removeStoredToken();
    if (onSessionKicked) onSessionKicked();
    throw new Error('SESSION_KICKED');
  }

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error?.message || data.message || 'Request failed');
  }

  return data;
};

// Auth API
export const auth = {
  async login(email, password) {
    const response = await apiFetch('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    if (response.data?.token) {
      setStoredToken(response.data.token);
    }
    return response.data;
  },

  async signup(email, password, name, invitationToken) {
    const response = await apiFetch('/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password, name, invitationToken }),
    });
    if (response.data?.token) {
      setStoredToken(response.data.token);
    }
    return response.data;
  },

  async updateProfile(data) {
    const response = await apiFetch('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  async verifyPin(pin) {
    const response = await apiFetch('/auth/verify-pin', {
      method: 'POST',
      body: JSON.stringify({ pin }),
    });
    return response.data;
  },

  async getStatus() {
    const response = await apiFetch('/auth/status');
    return response.data;
  },

  async lock() {
    const response = await apiFetch('/auth/lock', { method: 'POST' });
    return response.data;
  },

  async logout() {
    try {
      await apiFetch('/auth/logout', { method: 'POST' });
    } catch (err) {
      // Ignore - session may already be invalidated
    } finally {
      removeStoredToken();
    }
  },

  isAuthenticated() {
    const token = initToken();
    return !!token;
  },

  getToken() {
    return authToken;
  },
};

// Loans API
export const loans = {
  async list() {
    const response = await apiFetch('/loans');
    return response.data;
  },

  async get(loanId) {
    const response = await apiFetch(`/loans/${loanId}`);
    return response.data;
  },

  async create(loanData) {
    const response = await apiFetch('/loans', {
      method: 'POST',
      body: JSON.stringify(loanData),
    });
    return response.data;
  },

  async update(loanId, loanData) {
    const response = await apiFetch(`/loans/${loanId}`, {
      method: 'PUT',
      body: JSON.stringify(loanData),
    });
    return response.data;
  },

  async delete(loanId) {
    const response = await apiFetch(`/loans/${loanId}`, {
      method: 'DELETE',
    });
    return response.data;
  },
};

// Payments API
export const payments = {
  async create(loanId, paymentData) {
    const response = await apiFetch(`/loans/${loanId}/payments`, {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
    return response.data;
  },

  async createCapitalPayment(loanId, paymentData) {
    const response = await apiFetch(`/loans/${loanId}/payments/capital`, {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
    return response.data;
  },

  async refinance(loanId, paymentId, refinanceData) {
    const response = await apiFetch(`/loans/${loanId}/payments/${paymentId}/refinance`, {
      method: 'POST',
      body: JSON.stringify(refinanceData),
    });
    return response.data;
  },

  async revert(loanId, paymentId) {
    const response = await apiFetch(`/loans/${loanId}/payments/${paymentId}/revert`, {
      method: 'POST',
    });
    return response.data;
  },
};

// Dashboard API
export const dashboard = {
  async get() {
    const response = await apiFetch('/dashboard');
    return response.data;
  },
};

// Settings API
export const settings = {
  async get() {
    const response = await apiFetch('/settings');
    return response.data;
  },
  async update(data) {
    const response = await apiFetch('/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data;
  },
};

// Receipts API
export const receipts = {
  async getUploadUrl(data) {
    const response = await apiFetch('/receipts/upload-url', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  async getDownloadUrl(data) {
    const response = await apiFetch('/receipts/download-url', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  },
};

// Notes API
export const notes = {
  async list() {
    const response = await apiFetch('/notes');
    return response.data;
  },

  async get(noteId) {
    const response = await apiFetch(`/notes/${noteId}`);
    return response.data;
  },

  async create(noteData) {
    const response = await apiFetch('/notes', {
      method: 'POST',
      body: JSON.stringify(noteData),
    });
    return response.data;
  },

  async update(noteId, noteData) {
    const response = await apiFetch(`/notes/${noteId}`, {
      method: 'PUT',
      body: JSON.stringify(noteData),
    });
    return response.data;
  },

  async delete(noteId) {
    const response = await apiFetch(`/notes/${noteId}`, {
      method: 'DELETE',
    });
    return response.data;
  },
};

export default {
  auth,
  loans,
  payments,
  dashboard,
  settings,
  receipts,
  notes,
};
